import { ApolloClient, ApolloCache, NormalizedCacheObject } from "@apollo/client";
import { Deduction, DeductionProps, ApprovalRemittance, ApprovalRemittanceProps } from "@/core/entities/deduction";
import {
  IDeductionRepository,
  IDeductionsByRemittanceItemRepositoryParams,
  IDeductionsByRemittanceItemRepositoryResponse,
  IUpdateDeductionDetailsRepositoryParams,
  IUpdateDeductionDetailsRepositoryResponse,
  IDeductionAttachDocumentsParams,
  IDeductionAttachDocumentsResponse,
  IUpdateDeductionIntelligenceRepositoryParams,
  IUpdateDeductionIntelligenceRepositoryResponse,
  IDeductionDeleteDocumentsResponse,
  IDeductionDeleteDocumentsParams,
  ISendForApprovalRepositoryResponse,
  ISendForApprovalRepositoryParams,
  IGetApprovalsRepositoryParams,
  IGetApprovalsRepositoryResponse,
  IGetApprovalsByRemittanceRepositoryResponse,
  IGetAccountingDataRepositoryParams,
  IGetAccountingDataRepositoryResponse,
  IDeductionColumns,
  ISendForApprovalBulkRepositoryResponse,
  IResetApprovalWorkflowRepositoryParams,
  IResetApprovalWorkflowRepositoryResponse,
  ISendRemittanceForApprovalRepositoryParams,
  ISendRemittanceForApprovalRepositoryResponse,
  ICheckRemittanceForApprovalRepositoryResponse,
  IDeductionByDeductionIdRepositoryResponse,
  IDeductionByDeductionIdRepositoryParams,
} from "@/core/repositories/deduction";
import {
  GET_DEDUCTION_BY_REMITTANCE_ITEM_ID,
  UPDATE_DEDUCTION_DETAILS,
  ATTACH_DOCUMENTS_TO_DEDUCTION,
  UPDATE_DEDUCTION_INTELLIGENCE,
  DELETE_DEDUCTION_DOCUMENTS,
  SEND_FOR_APPROVAL,
  GET_APPROVALS,
  GET_APPROVALS_BY_REMITTANCE,
  SEND_FOR_APPROVAL_BULK,
  SEND_REMITTANCE_FOR_APPROVAL,
  CHECK_REMITTANCE_FOR_APPROVAL,
  RESET_APPROVAL_WORKFLOW,
  GET_ACCOUNTING_DATA,
  GET_DEDUCTION_BY_DEDUCTION_ID,
} from "@/core/graphql/deduction";
import { createLogger, ILogger } from "@/infrastructure/services/logger";
import { AppConfig } from "@/infrastructure/services/config";

const _appConfig = AppConfig.getInstance();

const TIME_ZONE = _appConfig.TIMEZONE;
interface IDeductionQueryResponse {
  deductions: {
    count: number;
    items: DeductionProps[];
  };
}

interface IGetApprovalsQueryResponse {
  getApprovals: {
    total: number;
    page: number;
    limit: number;
    items: DeductionProps[];
    columns: IDeductionColumns[];
  };
}

interface IGetApprovalsByRemittanceQueryResponse {
  getApprovalsByRemittance: {
    total: number;
    page: number;
    limit: number;
    items: ApprovalRemittanceProps[];
    columns: IDeductionColumns[];
  };
}

interface IGetAccountingDataQueryResponse {
  getAccountingData: {
    total: number;
    page: number;
    limit: number;
    items: DeductionProps[];
    columns: IDeductionColumns[];
  };
}

export class DeductionRepository implements IDeductionRepository {
  private readonly logger: ILogger = createLogger("DeductionRepository");
  constructor(private readonly client: ApolloClient) { }

  public async getDeductionsByRemittanceItem(
    params: IDeductionsByRemittanceItemRepositoryParams,
    refetch: boolean = false
  ): Promise<IDeductionsByRemittanceItemRepositoryResponse> {
    const { data } = await this.client.query<IDeductionQueryResponse>({
      query: GET_DEDUCTION_BY_REMITTANCE_ITEM_ID,
      variables: { params },
      fetchPolicy: refetch ? "network-only" : "cache-first",
    });

    if (!data) {
      throw new Error("Failed to get deductions");
    }

    const deductionsResponse = data.deductions;

    return {
      deductions: deductionsResponse.items.map(deduction => new Deduction(deduction)),
      count: deductionsResponse.count,
    };
  }

  public async getDeductionByDeductionId(
    params: IDeductionByDeductionIdRepositoryParams,
  ): Promise<IDeductionByDeductionIdRepositoryResponse> {
    const { data } = await this.client.query<IDeductionByDeductionIdRepositoryResponse>({
      query: GET_DEDUCTION_BY_DEDUCTION_ID,
      variables: { params },
    });

    if (!data) {
      throw new Error("Failed to get deduction by deduction id");
    }

    const cache = this.client.cache;
    this.evictDeductionsByRemittance(cache, data.deduction.remittanceItemId);
    this.evictDeduction(cache, data.deduction.id);

    return {
      deduction: new Deduction(data.deduction),
    };
  }

  public async attachDocumentsToDeduction(
    params: IDeductionAttachDocumentsParams,
    currentPage: number
  ): Promise<IDeductionAttachDocumentsResponse> {
    const { data } = await this.client.mutate<IDeductionAttachDocumentsResponse>({
      mutation: ATTACH_DOCUMENTS_TO_DEDUCTION,
      variables: {
        params: {
          deduction_id: params.deduction_id,
          files: params.files,
        },
      },
      update: (cache, { data }) => {
        if (data?.attachDocument) {
          this.evictCacheForDeductionAndRemittance(
            cache,
            data.attachDocument.id,
            data.attachDocument.remittanceItemId,
            currentPage
          );
        }
      },
    });

    if (!data) {
      throw new Error("Failed to attach documents to deduction");
    }

    return {
      attachDocument: new Deduction(data.attachDocument),
    };
  }

  private async evictCacheForDeductionAndRemittance(
    cache: ApolloCache,
    deductionId: number,
    remittanceItemId: number,
    currentPage: number
  ) {
    const rawCache = cache.extract() as NormalizedCacheObject;
    const rootQuery = rawCache?.ROOT_QUERY;
    if (!rootQuery) return;

    for (const key of Object.keys(rootQuery)) {
      this.evictPaginatedField(cache, key, "remittances", currentPage);
      this.evictPaginatedField(cache, key, "remittanceItems", currentPage);
    }

    this.evictDeduction(cache, deductionId);
    this.evictDeductionsByRemittance(cache, remittanceItemId);

    cache.gc();
  }

  private evictPaginatedField(
    cache: ApolloCache,
    key: string,
    fieldName: "remittances" | "remittanceItems",
    currentPage: number
  ) {
    const match = new RegExp(String.raw`^${fieldName}\((.*)\)$`).exec(key);
    if (!match) return;

    const args = this.safeParseCacheArgs(match[1], fieldName, key);
    if (!args || args.params?.page_number !== currentPage) return;

    cache.evict({
      id: "ROOT_QUERY",
      fieldName,
      args,
    });
  }

  private safeParseCacheArgs(matchGroup: string, fieldName: string, key: string): Record<string, any> | null {
    try {
      return JSON.parse(matchGroup);
    } catch (e) {
      this.logger.warn(`DeductionRepository: Failed to parse ${fieldName} key:`, key, e);
      return null;
    }
  }

  private evictDeduction(cache: ApolloCache, deductionId: number) {
    cache.evict({
      id: cache.identify({
        __typename: "DeductionType",
        id: deductionId,
      }),
    });
  }

  private evictDeductionsByRemittance(cache: ApolloCache, remittanceItemId: number) {
    cache.evict({
      id: "ROOT_QUERY",
      fieldName: "deductions",
      args: {
        params: { remittance_item_id: remittanceItemId },
      },
    });
  }

  public async updateDeductionDetails(
    params: IUpdateDeductionDetailsRepositoryParams,
    currentPage: number
  ): Promise<IUpdateDeductionDetailsRepositoryResponse> {
    const { data } = await this.client.mutate<IUpdateDeductionDetailsRepositoryResponse>({
      mutation: UPDATE_DEDUCTION_DETAILS,
      variables: { params },
      update: (cache, { data }) => {
        if (data?.updateDeductionDetails) {
          this.evictCacheForDeductionAndRemittance(
            cache,
            data.updateDeductionDetails.id,
            data.updateDeductionDetails.remittanceItemId,
            currentPage
          );
        }
      },
    });

    const updateDeductionDetailsResponse = data?.updateDeductionDetails;

    if (!updateDeductionDetailsResponse) {
      throw new Error("Failed to update deduction details");
    }

    return {
      updateDeductionDetails: new Deduction(updateDeductionDetailsResponse),
    };
  }

  public async updateDeductionIntelligence(
    params: IUpdateDeductionIntelligenceRepositoryParams,
    currentPage: number
  ): Promise<IUpdateDeductionIntelligenceRepositoryResponse> {
    const { data } = await this.client.mutate<IUpdateDeductionIntelligenceRepositoryResponse>({
      mutation: UPDATE_DEDUCTION_INTELLIGENCE,
      variables: { params },
      update: (cache, { data }) => {
        if (data?.updateDeductionIntelligence) {
          this.evictCacheForDeductionAndRemittance(
            cache,
            data.updateDeductionIntelligence.id,
            data.updateDeductionIntelligence.remittanceItemId,
            currentPage
          );
        }
      },
    });

    const updateDeductionIntelligenceResponse = data?.updateDeductionIntelligence;

    if (!updateDeductionIntelligenceResponse) {
      throw new Error("Failed to update deduction intelligence");
    }

    return {
      updateDeductionIntelligence: new Deduction(updateDeductionIntelligenceResponse),
    };
  }

  public async deleteDeductionDocuments(
    params: IDeductionDeleteDocumentsParams,
    currentPage: number
  ): Promise<IDeductionDeleteDocumentsResponse> {
    const { data } = await this.client.mutate<IDeductionDeleteDocumentsResponse>({
      mutation: DELETE_DEDUCTION_DOCUMENTS,
      variables: { params },
      update: (cache, { data }) => {
        if (data?.deleteDeductionDocuments) {
          this.evictCacheForDeductionAndRemittance(
            cache,
            data.deleteDeductionDocuments.id,
            data.deleteDeductionDocuments.remittanceItemId,
            currentPage
          );
        }
      },
    });

    if (!data) {
      throw new Error("Failed to delete deduction documents");
    }

    return { deleteDeductionDocuments: new Deduction(data.deleteDeductionDocuments) };
  }

  public async sendForApproval(
    params: ISendForApprovalRepositoryParams,
    currentPage: number
  ): Promise<ISendForApprovalRepositoryResponse> {
    const { data } = await this.client.mutate<ISendForApprovalRepositoryResponse>({
      mutation: SEND_FOR_APPROVAL,
      variables: { params },
      update: (cache, { data }) => {
        if (data?.sendForApproval) {
          this.evictCacheForDeductionAndRemittance(
            cache,
            data.sendForApproval.id,
            data.sendForApproval.remittanceItemId,
            currentPage
          );
        }
      },
    });

    if (!data) {
      throw new Error("Failed to send for approval");
    }

    return { sendForApproval: new Deduction(data.sendForApproval) };
  }

  public async sendForApprovalBulk(
    params: ISendForApprovalRepositoryParams[],
    currentPage: number
  ): Promise<ISendForApprovalBulkRepositoryResponse> {
    const { data } = await this.client.mutate<ISendForApprovalBulkRepositoryResponse>({
      mutation: SEND_FOR_APPROVAL_BULK,
      variables: {
        params: {
          deductions: params.map(param => ({
            deduction_id: param.deduction_id,
            remittance_item_id: param.remittance_item_id,
            comment: param.comment,
            action: param.action,
          })),
        },
      },
      update: (cache, { data }) => {
        if (data?.bulkSendForApproval) {
          data.bulkSendForApproval.forEach(deduction => {
            this.evictCacheForDeductionAndRemittance(cache, deduction.id, deduction.remittanceItemId, currentPage);
          });
        }
      },
    });

    if (!data) {
      throw new Error("Failed to send for approval bulk");
    }

    return { bulkSendForApproval: data.bulkSendForApproval.map(deduction => new Deduction(deduction)) };
  }

  public async sendRemittanceForApproval(
    params: ISendRemittanceForApprovalRepositoryParams,
    currentPage: number
  ): Promise<ISendRemittanceForApprovalRepositoryResponse> {
    const { data } = await this.client.mutate<ISendRemittanceForApprovalRepositoryResponse>({
      mutation: SEND_REMITTANCE_FOR_APPROVAL,
      variables: {
        params: {
          remittance_id: params.remittance_id,
          comment: params.comment,
          action: params.action,
        },
      },
      update: (cache, { data }) => {
        if (data?.sendRemittanceForApproval) {
          data.sendRemittanceForApproval.forEach(deduction => {
            this.evictCacheForDeductionAndRemittance(cache, deduction.id, deduction.remittanceItemId, currentPage);
          });
        }
      },
    });

    if (!data) {
      throw new Error("Failed to send remittance for approval");
    }

    return {
      sendRemittanceForApproval: data.sendRemittanceForApproval.map(deduction => new Deduction(deduction)),
    };
  }

  public async checkRemittanceForApproval(
    params: ISendRemittanceForApprovalRepositoryParams
  ): Promise<ICheckRemittanceForApprovalRepositoryResponse> {
    const { data } = await this.client.query<ICheckRemittanceForApprovalRepositoryResponse>({
      query: CHECK_REMITTANCE_FOR_APPROVAL,
      variables: {
        params: {
          remittance_id: params.remittance_id,
          comment: params.comment,
          action: params.action,
        },
      },
      fetchPolicy: "network-only",
    });

    if (!data?.checkRemittanceForApproval) {
      throw new Error("Failed to check remittance for approval");
    }

    return {
      checkRemittanceForApproval: data.checkRemittanceForApproval,
    };
  }

  public async resetApprovalWorkflow(
    params: IResetApprovalWorkflowRepositoryParams
  ): Promise<IResetApprovalWorkflowRepositoryResponse> {
    const { data } = await this.client.mutate<IResetApprovalWorkflowRepositoryResponse>({
      mutation: RESET_APPROVAL_WORKFLOW,
      variables: { params: { deductionId: params.deductionId } },
    });

    if (!data?.resetApprovalWorkflow) {
      throw new Error("Failed to reset approval workflow");
    }

    return { resetApprovalWorkflow: data.resetApprovalWorkflow };
  }

  public async getApprovals(
    params: IGetApprovalsRepositoryParams,
    refetch: boolean = false
  ): Promise<IGetApprovalsRepositoryResponse> {
    const { data } = await this.client.query<IGetApprovalsQueryResponse>({
      query: GET_APPROVALS,
      variables: { params: { ...params, time_zone: TIME_ZONE } },
      fetchPolicy: refetch ? "network-only" : "cache-first",
    });

    if (!data) {
      throw new Error("Failed to get approvals");
    }

    return {
      getApprovals: data.getApprovals.items.map(deduction => new Deduction(deduction)),
      totalCount: data.getApprovals.total,
      currentPage: data.getApprovals.page,
      pageLimit: data.getApprovals.limit,
      columns: data.getApprovals.columns,
    };
  }


  public async getApprovalsByRemittance(
    params: IGetApprovalsRepositoryParams,
    refetch: boolean = false
  ): Promise<IGetApprovalsByRemittanceRepositoryResponse> {
    const { data } = await this.client.query<IGetApprovalsByRemittanceQueryResponse>({
      query: GET_APPROVALS_BY_REMITTANCE,
      variables: { params: { ...params, time_zone: TIME_ZONE } },
      fetchPolicy: refetch ? "network-only" : "cache-first",
    });

    if (!data) {
      throw new Error("Failed to get approvals by remittance");
    }

    return {
      getApprovalsByRemittance: data.getApprovalsByRemittance.items.map(
        remittance => new ApprovalRemittance(remittance)
      ),
      totalCount: data.getApprovalsByRemittance.total,
      currentPage: data.getApprovalsByRemittance.page,
      pageLimit: data.getApprovalsByRemittance.limit,
      columns: data.getApprovalsByRemittance.columns,
    };
  }

  public async getAccountingData(
    params: IGetAccountingDataRepositoryParams,
    refetch: boolean = false
  ): Promise<IGetAccountingDataRepositoryResponse> {
    const { data } = await this.client.query<IGetAccountingDataQueryResponse>({
      query: GET_ACCOUNTING_DATA,
      variables: { params: { ...params, time_zone: TIME_ZONE } },
      fetchPolicy: refetch ? "network-only" : "cache-first",
    });

    if (!data) {
      throw new Error("Failed to get accounting data");
    }

    return {
      getAccountingData: data.getAccountingData.items.map(deduction => new Deduction(deduction)),
      totalCount: data.getAccountingData.total,
      currentPage: data.getAccountingData.page,
      pageLimit: data.getAccountingData.limit,
      columns: data.getAccountingData.columns,
    };
  }
}
